import type { InternalNetworkAdEntityKindSchema } from '@terra/shared-schemas-orchestrator';
import type { z } from 'zod';

type AdEntityKind = z.infer<typeof InternalNetworkAdEntityKindSchema>;

export const normalizeAdObjectId = (objectId: string) =>
  objectId.trim().toLowerCase();

const NODE_ID_ENTITY_KIND_BY_ENTITY_KIND: Record<string, string> = {
  DOMAIN_CONTROLLER: 'COMPUTER',
};

export const resolveAdGraphNodeIdEntityKind = (
  entityKind: AdEntityKind | string,
): string => NODE_ID_ENTITY_KIND_BY_ENTITY_KIND[entityKind] ?? entityKind;

export const makeAdGraphNodeId = ({
  entityKind,
  objectId,
}: {
  entityKind: AdEntityKind | string;
  objectId: string;
}) =>
  `${resolveAdGraphNodeIdEntityKind(entityKind)}:${normalizeAdObjectId(objectId)}`;

export const parseAdGraphNodeId = (
  nodeId: string,
): { entityKind: string; objectId: string } | null => {
  const separatorIndex = nodeId.indexOf(':');

  if (separatorIndex <= 0 || separatorIndex >= nodeId.length - 1) {
    return null;
  }

  return {
    entityKind: nodeId.slice(0, separatorIndex),
    objectId: normalizeAdObjectId(nodeId.slice(separatorIndex + 1)),
  };
};

const KIND_PREFIXED_OBJECT_ID = /^[A-Za-z_]+:(.+)$/;

export const coerceAdGraphObjectId = (input: string): string | null => {
  const trimmed = input.trim();

  if (trimmed.length === 0) {
    return null;
  }

  const prefixed = KIND_PREFIXED_OBJECT_ID.exec(trimmed);

  if (prefixed?.[1]) {
    const normalized = normalizeAdObjectId(prefixed[1]);

    return normalized.length > 0 ? normalized : null;
  }

  if (trimmed.includes(':')) {
    return null;
  }

  const normalized = normalizeAdObjectId(trimmed);

  return normalized.length > 0 ? normalized : null;
};
